import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, ButtonGroup } from "react-bootstrap";
import { Group, Layer, Stage } from "react-konva";
import { Maximize2, Minimize2, Minus, Plus, RotateCcw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { TrackTraceAsset, TrackTraceMapPayload, TrackTraceZone } from "../../trackTrace/types";
import { floorMapViewportToCanvas } from "../../trackTrace/mapUtils";
import AssetMarkerLayer from "./AssetMarkerLayer";
import FloorPlanImageLayer from "./FloorPlanImageLayer";
import ZonePolygonLayer from "./ZonePolygonLayer";

interface Props {
    payload: TrackTraceMapPayload;
    selectedAssetId?: string | null;
    selectedZoneId?: number | null;
    onSelectAsset: (asset: TrackTraceAsset) => void;
    onSelectZone: (zone: TrackTraceZone) => void;
    onImageError: () => void;
}

const MAX_ZOOM = 10;

const FloorPlanCanvas: React.FC<Props> = ({ payload, selectedAssetId, selectedZoneId, onSelectAsset, onSelectZone, onImageError }) => {
    const { i18n } = useTranslation();
    const t = (key: string, options?: object) => (i18n.t as unknown as (key: string, options?: object) => string)(key, options);
    const floorMap = payload.floor_map;
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const [zoom, setZoom] = useState(1);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [canvasSize, setCanvasSize] = useState({ width: window.innerWidth - 16, height: 0 });
    const width = canvasSize.width;
    const viewport = floorMap ? floorMapViewportToCanvas(floorMap) : { x: 0, y: 0, width: 800, height: 600, defaultZoom: 1 };
    const naturalWidth = viewport.width;
    const naturalHeight = viewport.height;
    const baseScale = useMemo(() => Math.min(width / naturalWidth, 1), [width, naturalWidth]);
    const stageHeight = isFullscreen && canvasSize.height ? canvasSize.height : Math.max(360, naturalHeight * baseScale);

    useEffect(() => {
        setZoom(viewport.defaultZoom);
        setPosition({ x: 0, y: 0 });
    }, [floorMap?.id, viewport.x, viewport.y, viewport.width, viewport.height, viewport.defaultZoom]);

    useEffect(() => {
        const updateSize = () => {
            const wrapper = wrapperRef.current;
            setCanvasSize({
                width: wrapper?.clientWidth || window.innerWidth - 16,
                height: wrapper?.clientHeight || 0,
            });
        };
        const handleFullscreenChange = () => {
            setIsFullscreen(document.fullscreenElement === wrapperRef.current);
            window.setTimeout(updateSize, 0);
        };

        updateSize();
        window.addEventListener('resize', updateSize);
        document.addEventListener('fullscreenchange', handleFullscreenChange);

        return () => {
            window.removeEventListener('resize', updateSize);
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
        };
    }, []);

    if (!floorMap) {
        return <div className="map-empty-state">{t('track_trace.map.empty.no_floor_map_configured')}</div>;
    }

    const scale = baseScale * zoom;
    const toggleFullscreen = async () => {
        const wrapper = wrapperRef.current;
        if (!wrapper) return;

        if (document.fullscreenElement === wrapper) {
            await document.exitFullscreen();
            return;
        }

        await wrapper.requestFullscreen();
    };

    return (
        <div className="floor-plan-wrap" ref={wrapperRef}>
            <div className="map-zoom-controls">
                <ButtonGroup size="sm">
                    <Button variant="light" onClick={() => setZoom((value) => Math.min(value + 0.2, MAX_ZOOM))} aria-label={t('track_trace.map.actions.zoom_in')}><Plus size={16} /></Button>
                    <Button variant="light" onClick={() => setZoom((value) => Math.max(value - 0.2, 0.6))} aria-label={t('track_trace.map.actions.zoom_out')}><Minus size={16} /></Button>
                    <Button variant="light" onClick={() => { setZoom(viewport.defaultZoom); setPosition({ x: 0, y: 0 }); }} aria-label={t('track_trace.map.actions.reset_zoom')}><RotateCcw size={16} /></Button>
                    <Button variant="light" onClick={toggleFullscreen} aria-label={isFullscreen ? t('track_trace.map.actions.exit_fullscreen') : t('track_trace.map.actions.enter_fullscreen')}>
                        {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                    </Button>
                </ButtonGroup>
            </div>
            <Stage
                width={width}
                height={stageHeight}
                draggable
                x={position.x}
                y={position.y}
                scaleX={scale}
                scaleY={scale}
                onDragEnd={(event) => setPosition({ x: event.target.x(), y: event.target.y() })}
            >
                <Layer>
                    <Group clipX={0} clipY={0} clipWidth={viewport.width} clipHeight={viewport.height}>
                        <Group x={-viewport.x} y={-viewport.y}>
                            <FloorPlanImageLayer floorMap={floorMap} onLoadError={onImageError} />
                            <ZonePolygonLayer zones={payload.zones} floorMap={floorMap} selectedZoneId={selectedZoneId} onSelectZone={onSelectZone} />
                            <AssetMarkerLayer assets={payload.assets} floorMap={floorMap} canvasScale={scale} selectedAssetId={selectedAssetId} onSelectAsset={onSelectAsset} />
                        </Group>
                    </Group>
                </Layer>
            </Stage>
        </div>
    );
};

export default FloorPlanCanvas;
